/**
 * Idempotency keys. SPEC.md §12.4, §10.5, §38.
 *
 * One key per *intended* action — every tap, and every repeat of a
 * press-and-hold, gets its own. Reusing a key would make a deliberate double
 * tap look like a retry and silently drop a point change; generating one per
 * network attempt would let a retry post twice.
 */

import {
  IDEMPOTENCY_KEY_MAX_LENGTH,
  IDEMPOTENCY_KEY_MIN_LENGTH,
} from '@card-counter/shared';

const HEX = '0123456789abcdef';

/**
 * crypto.randomUUID is unavailable outside secure contexts, which includes the
 * plain-http LAN address a host might share at the table, so the CSPRNG and then
 * Math.random back it up. Uniqueness only has to hold within one session.
 */
function randomHex(bytes: number): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    const buffer = new Uint8Array(bytes);
    cryptoApi.getRandomValues(buffer);
    let out = '';
    for (const byte of buffer) out += byte.toString(16).padStart(2, '0');
    return out;
  }
  let out = '';
  for (let i = 0; i < bytes * 2; i += 1) {
    out += HEX[Math.floor(Math.random() * 16)];
  }
  return out;
}

function rawKey(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  // 32 hex chars carries the same entropy budget as a v4 UUID's random bits.
  return randomHex(16);
}

/** A fresh key inside the shared length bounds. §38 */
export function newIdempotencyKey(): string {
  let key = rawKey();
  while (key.length < IDEMPOTENCY_KEY_MIN_LENGTH) {
    key += randomHex(8);
  }
  return key.slice(0, IDEMPOTENCY_KEY_MAX_LENGTH);
}
